import React from 'react';
import { Translate } from 'react-localize-redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * SHER-636 — clinician-facing amendment history for a clinical submission.
 *
 * Surfaces the SubmissionEditHistory audit trail (already captured server-side)
 * so an edit reads as an attributed amendment — who changed it, when, why, and
 * the before/after per field — rather than a silent overwrite. Cross-author
 * edits (a colleague amending a peer's note in the same care episode) are badged
 * so the attribution is unambiguous. Purely presentational; the parent supplies
 * the data plus loading/error state.
 */
export interface AmendmentChange {
    fieldName: string;
    oldValue: string | null;
    newValue: string | null;
}

export interface AmendmentEntry {
    editedByName: string;
    editedAt: string; // ISO timestamp
    editReason: string;
    overrideOtherAuthor: boolean;
    changes: AmendmentChange[];
}

export interface AmendmentHistoryProps {
    amendments: AmendmentEntry[];
    loading?: boolean;
    error?: boolean;
}

function formatDate(iso: string): string {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export const AmendmentHistory: React.FC<AmendmentHistoryProps> = ({ amendments, loading, error }) => {
    let body: React.ReactNode;

    if (loading) {
        body = (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} aria-label="loading" />
            </Box>
        );
    } else if (error) {
        body = (
            <Alert severity="error">
                <Translate id="hospital.patient.amendment-history.error" />
            </Alert>
        );
    } else if (!amendments || amendments.length === 0) {
        body = (
            <Typography variant="body2" color="text.secondary">
                <Translate id="hospital.patient.amendment-history.empty" />
            </Typography>
        );
    } else {
        body = (
            <Box>
                {amendments.map((a, idx) => (
                    <Box key={idx} sx={{ py: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.editedByName}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatDate(a.editedAt)}</Typography>
                            {a.overrideOtherAuthor && (
                                <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    label={<Translate id="hospital.patient.amendment-history.cross-author" />}
                                />
                            )}
                        </Box>
                        {a.editReason && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                <Translate id="hospital.patient.amendment-history.reason" />: {a.editReason}
                            </Typography>
                        )}
                        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                            {a.changes.map((c, ci) => (
                                <Typography key={ci} component="li" variant="caption" color="text.secondary">
                                    {c.fieldName}: {c.oldValue ?? '—'} → {c.newValue ?? '—'}
                                </Typography>
                            ))}
                        </Box>
                        {idx < amendments.length - 1 && <Divider sx={{ mt: 1 }} />}
                    </Box>
                ))}
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                <Translate id="hospital.patient.amendment-history.title" />
            </Typography>
            {body}
        </Box>
    );
};

export default AmendmentHistory;
